import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Calendar, Clock, Video, Phone, User, Building2,
  CheckCircle, XCircle, AlertCircle, RefreshCw, MessageSquare,
  ChevronDown, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const MeetingsPage = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState({ pending: [], confirmed: [], past: [] });
  const [counts, setCounts] = useState({ pending: 0, confirmed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    // Check if logged in
    const storedAssistant = sessionStorage.getItem('assistant');
    if (!storedAssistant) {
      navigate('/assistant-login');
      return;
    }
    fetchMeetings();
  }, [navigate]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/meetings`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        const data = await res.json();
        setMeetings({
          pending: data.pending || [],
          confirmed: data.confirmed || [],
          past: data.past || []
        });
        setCounts(data.counts || { pending: 0, confirmed: 0, total: 0 });
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateMeetingStatus = async (clientId, meetingId, newStatus) => {
    setUpdating(meetingId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/clients/${clientId}/meetings/${meetingId}?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (res.ok) {
        fetchMeetings(); // Refresh
      }
    } catch (err) {
      console.error('Error updating meeting:', err);
    } finally {
      setUpdating(null);
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minute} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const MeetingCard = ({ meeting }) => {
    const isPending = meeting.status === 'pending';
    const isConfirmed = meeting.status === 'confirmed';
    const isCancelled = meeting.status === 'cancelled';

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isPending ? 'bg-amber-100 text-amber-600' :
              isConfirmed ? 'bg-green-100 text-green-600' :
              'bg-gray-100 text-gray-400'
            }`}>
              {meeting.meeting_type === 'video' ? <Video size={22} /> : <Phone size={22} />}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{meeting.title || 'Check-in Meeting'}</h3>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {formatDate(meeting.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {formatTime(meeting.time)}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-sm">
                <span className="flex items-center gap-1 text-gray-600">
                  <User size={14} />
                  {meeting.client_name || 'Unknown'}
                </span>
                {meeting.business_name && (
                  <span className="flex items-center gap-1 text-gray-400">
                    <Building2 size={14} />
                    {meeting.business_name}
                  </span>
                )}
              </div>
              {meeting.notes && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                  <p className="text-xs text-gray-400 mb-1 font-medium">Client Notes:</p>
                  {meeting.notes}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isPending ? 'bg-amber-100 text-amber-700' :
              isConfirmed ? 'bg-green-100 text-green-700' :
              'bg-gray-100 text-gray-500'
            }`}>
              {isPending ? 'Pending' : isConfirmed ? 'Confirmed' : 'Cancelled'}
            </span>

            {isPending && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => updateMeetingStatus(meeting.client_id, meeting.id, 'confirmed')}
                  disabled={updating === meeting.id}
                  className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  <CheckCircle size={14} />
                  Confirm
                </button>
                <button
                  onClick={() => updateMeetingStatus(meeting.client_id, meeting.id, 'cancelled')}
                  disabled={updating === meeting.id}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  <XCircle size={14} />
                  Decline
                </button>
              </div>
            )}

            {isConfirmed && (
              <button
                onClick={() => updateMeetingStatus(meeting.client_id, meeting.id, 'cancelled')}
                disabled={updating === meeting.id}
                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                Cancel Meeting
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const currentMeetings = activeTab === 'pending' ? meetings.pending :
                          activeTab === 'confirmed' ? meetings.confirmed :
                          meetings.past;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/a-dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Client Meetings</h1>
                <p className="text-sm text-gray-500">Manage meeting requests from clients</p>
              </div>
            </div>
            <button
              onClick={fetchMeetings}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={20} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{counts.pending}</p>
                <p className="text-sm text-gray-500">Pending</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{counts.confirmed}</p>
                <p className="text-sm text-gray-500">Upcoming</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Calendar size={20} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{counts.total}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'pending', label: 'Pending', count: meetings.pending.length },
            { id: 'confirmed', label: 'Upcoming', count: meetings.confirmed.length },
            { id: 'past', label: 'Past / Cancelled', count: meetings.past.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-gray-100'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Meetings List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Loading meetings...</p>
          </div>
        ) : currentMeetings.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Calendar size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No {activeTab} meetings</p>
            <p className="text-gray-400 text-sm mt-1">
              {activeTab === 'pending' ? 'New meeting requests will appear here' :
               activeTab === 'confirmed' ? 'Confirmed meetings will show here' :
               'Past and cancelled meetings will be listed here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {currentMeetings.map(meeting => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingsPage;
